import React, { useEffect, useMemo, useState } from "react";
import UserServices from "../../UserServices/UserServices";
import "./StaffProfileHistory.css";

const rowsOf = (response) => {
  const payload = response?.data?.data;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const formatDate = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const money = (value) => {
  const amount = Number(value || 0);
  return `Rs ${amount.toLocaleString("en-PK")}`;
};

const weekdayCount = (start, end) => {
  const cursor = new Date(start);
  const last = new Date(end);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(last.getTime()) || cursor > last) return 0;
  let count = 0;
  while (cursor <= last) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
};

const isoDay = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  const month = String(parsed.getMonth() + 1).padStart(2, "0");
  const day = String(parsed.getDate()).padStart(2, "0");
  return `${parsed.getFullYear()}-${month}-${day}`;
};

const Fact = ({ label, value }) => (
  <div className="employee-cv-fact">
    <span>{label}</span>
    <strong>{value || "—"}</strong>
  </div>
);

const Section = ({ title, children }) => (
  <section className="employee-cv-section">
    <h3>{title}</h3>
    {children}
  </section>
);

export default function StaffProfileHistory({ staffId, branchId, profile, entries = [] }) {
  const [loading, setLoading] = useState(true);
  const [promotions, setPromotions] = useState([]);
  const [fines, setFines] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [disciplinary, setDisciplinary] = useState([]);
  const [leaves, setLeaves] = useState([]);
  const [attendance, setAttendance] = useState([]);

  useEffect(() => {
    let active = true;
    const year = new Date().getFullYear();
    const historyDates = { startdate: "2000-01-01", enddate: `${year}-12-31` };
    const attendanceDates = {
      sdate: `${year}-01-01`,
      edate: isoDay(new Date()),
      start_time: "00:00:00",
      end_time: "23:59:59",
      gender: "",
    };

    const load = async () => {
      setLoading(true);
      const [promotionRes, fineRes, rewardRes, letterRes, leaveRes, attendanceRes] = await Promise.allSettled([
        UserServices.getStaffPromotions(branchId, 200, 1, staffId),
        UserServices.getProfileStaffFines(staffId, branchId, "1", 200, 1, historyDates, "Fine"),
        UserServices.getProfileStaffFines(staffId, branchId, "1", 200, 1, historyDates, "Reward"),
        UserServices.getCertificatesLetters(branchId, 200, 1, staffId),
        UserServices.getProfileLeaveApplication(branchId, staffId, 200, 1),
        UserServices.getEmployeeAttendence(branchId, attendanceDates, staffId, 500, 1),
      ]);
      if (!active) return;
      const valueOf = (result) => (result.status === "fulfilled" ? rowsOf(result.value) : []);
      const letters = valueOf(letterRes).filter((row) =>
        /warning|misconduct|disciplin|show cause|termination|suspension/i.test(
          `${row.document_type || ""} ${row.document_category || ""} ${row.subject || ""}`
        )
      );
      setPromotions(valueOf(promotionRes));
      setFines(valueOf(fineRes));
      setRewards(valueOf(rewardRes));
      setDisciplinary(letters);
      setLeaves(valueOf(leaveRes));
      setAttendance(valueOf(attendanceRes));
      setLoading(false);
    };

    load();
    return () => {
      active = false;
    };
  }, [staffId, branchId]);

  const attendanceSummary = useMemo(() => {
    const year = new Date().getFullYear();
    const start = `${year}-01-01`;
    const end = isoDay(new Date());
    const present = new Set(
      attendance.map((row) => isoDay(row.date)).filter(Boolean)
    );
    let leaveDays = 0;
    leaves
      .filter((row) => String(row.application_status || "").toLowerCase() === "approved")
      .forEach((row) => {
        const from = row.from && row.from > start ? row.from : start;
        const to = row.to && row.to < end ? row.to : end;
        leaveDays += weekdayCount(from, to);
      });
    const weekdays = weekdayCount(start, end);
    const presentDays = present.size;
    const absentDays = Math.max(weekdays - presentDays - leaveDays, 0);
    return { presentDays, leaveDays, absentDays, weekdays };
  }, [attendance, leaves]);

  const qualifications = entries.filter((row) =>
    ["Qualification", "Education"].includes(row.entry_type)
  );
  const experience = entries.filter((row) => row.entry_type === "Experience");
  const recentAttendance = [...attendance]
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")))
    .slice(0, 12);
  const fineTotal = fines.reduce((sum, row) => sum + Number(row.amount || 0), 0);
  const rewardTotal = rewards.reduce((sum, row) => sum + Number(row.amount || 0), 0);

  return (
    <div className="employee-cv">
      <div className="employee-cv-toolbar">
        <div>
          <span>Employee record</span>
          <h2>Complete profile</h2>
        </div>
        <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => window.print()}>
          Print profile
        </button>
      </div>

      <header className="employee-cv-header">
        <div
          className="employee-cv-photo"
          style={profile.image ? { backgroundImage: `url("${profile.image}")` } : undefined}
        />
        <div>
          <h1>{profile.name || "Employee"}</h1>
          <p>{[profile.designation, profile.department].filter(Boolean).join(" · ") || "Staff member"}</p>
          <div className="employee-cv-chips">
            <span>ID {profile.uid || "—"}</span>
            <span>{profile.branch || "Branch not set"}</span>
            <span>{profile.employmentStatus || "Active"}</span>
          </div>
        </div>
      </header>

      <Section title="Personal details">
        <div className="employee-cv-facts">
          <Fact label="Father's name" value={profile.fatherName} />
          <Fact label="Gender" value={profile.gender} />
          <Fact label="Date of birth" value={formatDate(profile.dob)} />
          <Fact label="CNIC" value={profile.cnic} />
          <Fact label="Blood group" value={profile.bloodGroup} />
          <Fact label="Phone" value={profile.phone} />
          <Fact label="Emergency contact" value={profile.emergency} />
          <Fact label="Personal email" value={profile.email} />
          <Fact label="Official email" value={profile.officialEmail} />
          <Fact label="City" value={profile.city} />
          <Fact label="Address" value={profile.address} />
        </div>
      </Section>

      <Section title="Employment">
        <div className="employee-cv-facts">
          <Fact label="Branch" value={profile.branch} />
          <Fact label="Department" value={profile.department} />
          <Fact label="Designation" value={profile.designation} />
          <Fact label="Joining date" value={formatDate(profile.joining)} />
          <Fact label="Employment status" value={profile.employmentStatus} />
          <Fact label="Current salary" value={profile.salary ? money(profile.salary) : "—"} />
        </div>
      </Section>

      <Section title={`Attendance summary, ${new Date().getFullYear()}`}>
        <div className="employee-cv-stats">
          <div>
            <strong>{loading ? "…" : attendanceSummary.presentDays}</strong>
            <span>Days with check-in</span>
          </div>
          <div>
            <strong>{loading ? "…" : attendanceSummary.leaveDays}</strong>
            <span>Approved leave days</span>
          </div>
          <div>
            <strong>{loading ? "…" : attendanceSummary.absentDays}</strong>
            <span>Weekdays with no check-in</span>
          </div>
        </div>
        <p className="employee-cv-note">
          Weekdays with no check-in are counted from recorded attendance and approved leave. Public holidays are not removed from this count.
        </p>
        {recentAttendance.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Check in</th>
                <th>Check out</th>
              </tr>
            </thead>
            <tbody>
              {recentAttendance.map((row, index) => (
                <tr key={`${row.date}-${index}`}>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.checkin_time_12h || row.checkin_time_24h || "—"}</td>
                  <td>{row.checkout_time_12h || row.checkout_time_24h || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">{loading ? "Loading attendance…" : "No check-ins recorded this year."}</div>
        )}
      </Section>

      <Section title="Promotions and role changes">
        {promotions.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Salary</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {promotions.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.date)}</td>
                  <td>{row.promotion_type || "—"}</td>
                  <td>{[row.pervs_depart, row.pervs_designation].filter(Boolean).join(" / ") || "—"}</td>
                  <td>{[row.new_depart, row.new_designation].filter(Boolean).join(" / ") || "—"}</td>
                  <td>{money(row.previous_salary)} → {money(row.new_salary)}</td>
                  <td>{row.details || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">{loading ? "Loading promotions…" : "No promotion history recorded."}</div>
        )}
      </Section>

      <Section title={`Disciplinary actions${disciplinary.length ? ` · ${disciplinary.length}` : ""}`}>
        {disciplinary.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Type</th>
                <th>Subject</th>
                <th>Code</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {disciplinary.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.issue_date)}</td>
                  <td>{row.document_type || "—"}</td>
                  <td>{row.subject || "—"}</td>
                  <td>{row.document_code || "—"}</td>
                  <td>{String(row.status) === "0" ? "Inactive" : "Active"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">{loading ? "Loading disciplinary actions…" : "No disciplinary actions recorded."}</div>
        )}
      </Section>

      <Section title={`Fines · ${money(fineTotal)}`}>
        {fines.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {fines.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.occurrence_date || row.date)}</td>
                  <td>{money(row.amount)}</td>
                  <td>{row.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">{loading ? "Loading fines…" : "No fines recorded."}</div>
        )}
      </Section>

      <Section title={`Rewards · ${money(rewardTotal)}`}>
        {rewards.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.occurrence_date || row.date)}</td>
                  <td>{money(row.amount)}</td>
                  <td>{row.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">{loading ? "Loading rewards…" : "No rewards recorded."}</div>
        )}
      </Section>

      <Section title="Leave history">
        {leaves.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Submitted</th>
                <th>Type</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Status</th>
                <th>Reason</th>
              </tr>
            </thead>
            <tbody>
              {leaves.map((row) => (
                <tr key={row.id}>
                  <td>{formatDate(row.submission_date || row.created_at)}</td>
                  <td>{row.leave_type || "—"}</td>
                  <td>{formatDate(row.from)}</td>
                  <td>{formatDate(row.to)}</td>
                  <td>{row.number_of_leaves || "—"}</td>
                  <td>{row.application_status || "—"}</td>
                  <td>{row.reason || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">{loading ? "Loading leave…" : "No leave applications recorded."}</div>
        )}
      </Section>

      <Section title="Education and qualifications">
        {qualifications.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Organization</th>
                <th>From</th>
                <th>To</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {qualifications.map((row) => (
                <tr key={row.id}>
                  <td>{row.title || "—"}</td>
                  <td>{row.organization || "—"}</td>
                  <td>{formatDate(row.start_date)}</td>
                  <td>{formatDate(row.end_date)}</td>
                  <td>{row.description || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">No education records added yet.</div>
        )}
      </Section>

      <Section title="Experience">
        {experience.length ? (
          <table className="employee-cv-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Organization</th>
                <th>From</th>
                <th>To</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {experience.map((row) => (
                <tr key={row.id}>
                  <td>{row.title || "—"}</td>
                  <td>{row.organization || "—"}</td>
                  <td>{formatDate(row.start_date)}</td>
                  <td>{formatDate(row.end_date)}</td>
                  <td>{row.description || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="employee-cv-empty">No experience records added yet.</div>
        )}
      </Section>
    </div>
  );
}
